import { Input as UiInput, Textarea as UiTextarea } from "@carry/ui";
import { type ReactNode } from "react";

export function Section({
  title,
  action,
  children,
}: {
  title: string;
  action?: ReactNode;
  children: ReactNode;
}) {
  return (
    <section
      style={{
        background: "var(--bg-raised)",
        border: "0.5px solid var(--rule)",
        borderRadius: 3,
        overflow: "hidden",
      }}
    >
      <div
        style={{
          padding: "14px 18px",
          borderBottom: "0.5px solid var(--rule-soft)",
          display: "flex",
          alignItems: "baseline",
          justifyContent: "space-between",
          gap: 12,
          background: "var(--bg-panel)",
        }}
      >
        <div className="eyebrow">{title}</div>
        {action}
      </div>
      <div style={{ padding: 18 }}>{children}</div>
    </section>
  );
}

export function Grid({ cols, children }: { cols: number; children: ReactNode }) {
  return (
    <div
      style={{
        display: "grid",
        gridTemplateColumns: `repeat(${cols}, 1fr)`,
        gap: 14,
      }}
    >
      {children}
    </div>
  );
}

export function Field({
  label,
  required,
  hint,
  badge,
  tone,
  fieldKey,
  span,
  errors,
  children,
}: {
  label: string;
  required?: boolean;
  hint?: string;
  badge?: string;
  tone?: "imported" | "warning" | "modified" | "master" | "backfilled";
  fieldKey?: string;
  span?: number;
  errors?: ReadonlyArray<unknown>;
  children: ReactNode;
}) {
  const errorMessage = formatFieldErrors(errors);
  // PDF 反映フィールドはバッジを出さず、入力欄を水色背景でハイライト (data-imported)。
  // 手動変更済みは黄色 (data-modified)。要確認・マスタ補完・自動補完はバッジを表示する。
  // マスタ補完 (Issue #613) と時刻の自動補完 (Issue #657) は注文書由来ではないため、
  // ハイライトに加えて補完であることをバッジで示す。
  const isImported = !!badge && (tone === "imported" || tone === "master" || tone === "backfilled");
  const isModified = !!badge && tone === "modified";
  return (
    <label
      data-order-field={fieldKey}
      data-imported={isImported ? "true" : undefined}
      data-modified={isModified ? "true" : undefined}
      style={{
        display: "flex",
        flexDirection: "column",
        gap: 4,
        gridColumn: span ? `span ${span}` : undefined,
      }}
    >
      <span
        style={{
          fontSize: 11,
          color: "var(--ink-3)",
          display: "flex",
          alignItems: "center",
          gap: 6,
        }}
      >
        {label}
        {required && (
          <span
            style={{
              display: "inline-flex",
              alignItems: "center",
              fontSize: 9,
              fontWeight: 700,
              color: "oklch(0.45 0.2 30)",
              background: "oklch(0.97 0.03 30)",
              border: "0.5px solid oklch(0.75 0.12 30)",
              borderRadius: 3,
              padding: "1px 5px",
              letterSpacing: "0.06em",
              lineHeight: "14px",
            }}
          >
            必須
          </span>
        )}
        {badge && (tone === "warning" || tone === "master" || tone === "backfilled") && (
          <span
            style={{
              display: "inline-flex",
              alignItems: "center",
              fontSize: 9,
              fontWeight: 700,
              color:
                tone === "master" || tone === "backfilled"
                  ? "oklch(0.45 0.12 240)"
                  : "oklch(0.5 0.17 45)",
              background: tone === "master" ? "oklch(0.97 0.03 240)" : "oklch(0.97 0.05 70)",
              border:
                tone === "master"
                  ? "0.5px solid oklch(0.75 0.08 240)"
                  : "0.5px solid oklch(0.75 0.12 60)",
              borderRadius: 3,
              padding: "1px 5px",
              lineHeight: "14px",
            }}
          >
            {badge}
          </span>
        )}
      </span>
      {children}
      {errorMessage && (
        <span role="alert" style={{ fontSize: 10, color: "var(--danger)", fontWeight: 500 }}>
          {errorMessage}
        </span>
      )}
      {hint && !errorMessage && (
        <span style={{ fontSize: 10, color: "var(--ink-3)", fontFamily: "var(--f-mono)" }}>
          {hint}
        </span>
      )}
    </label>
  );
}

export function formatFieldErrors(errors: ReadonlyArray<unknown> | undefined): string | null {
  if (!errors || errors.length === 0) return null;
  const first = errors.find((e) => e != null);
  if (first == null) return null;
  if (typeof first === "string") return first;
  if (typeof first === "object" && first && "message" in first) {
    const msg = (first as { message: unknown }).message;
    if (typeof msg === "string") return msg;
  }
  return String(first);
}

export function Input({
  value,
  onChange,
  onBlur,
  type,
  placeholder,
  min,
  align,
  compact,
  tone,
  "aria-label": ariaLabel,
}: {
  value: string;
  onChange: (v: string) => void;
  onBlur?: () => void;
  type?: "text" | "number" | "date";
  placeholder?: string;
  min?: number;
  align?: "left" | "right";
  compact?: boolean;
  tone?: "imported" | "modified";
  /** 同じラベルの欄が繰り返し並ぶ場合 (経由地 N 件など) に読み上げ・テストで識別するため。 */
  "aria-label"?: string;
}) {
  return (
    <UiInput
      type={type ?? "text"}
      value={value}
      onChange={(e) => onChange(e.target.value)}
      onBlur={onBlur}
      placeholder={placeholder}
      min={min}
      aria-label={ariaLabel}
      style={{
        height: compact ? 26 : 30,
        fontSize: compact ? 11 : 12,
        textAlign: align ?? "left",
        fontFamily: type === "number" || type === "date" ? "var(--f-mono)" : "inherit",
        background:
          tone === "imported"
            ? "var(--imported-field-bg)"
            : tone === "modified"
              ? "var(--modified-field-bg)"
              : undefined,
      }}
    />
  );
}

export function Textarea({
  value,
  onChange,
  placeholder,
  rows = 3,
}: {
  value: string;
  onChange: (v: string) => void;
  placeholder?: string;
  rows?: number;
}) {
  return (
    <UiTextarea
      value={value}
      onChange={(e) => onChange(e.target.value)}
      placeholder={placeholder}
      rows={rows}
      style={{ resize: "vertical", fontSize: 12 }}
    />
  );
}

export function toInt(v: string, fallback: number): number {
  const n = Number.parseInt(v, 10);
  return Number.isFinite(n) ? n : fallback;
}
